import useColor from "../../../Header/hooks/useColor";
import useChileCheck from "../../../Header/hooks/useChileCheck";
import { useStoreState } from "../../../Header/store/useStore";
import { MenuProps } from "../../../Header/types";
import { useMemo } from "react";
import CryptoIcon from "../assets/header/nav/crypto.svg?react";
import MarketIcon from "../assets/header/nav/market.svg?react";
import FuturesMainIcon from "../assets/header/nav/futures-main.svg?react";
import SpotIcon from "../assets/header/nav/spot.svg?react";
import SpotMainIcon from "../assets/header/nav/spot-main.svg?react";
import CopyTradingIcon from "../assets/header/nav/copy-trading.svg?react";
import EarnIcon from "../assets/header/nav/earn.svg?react";
import MoreIcon from "../assets/header/nav/more.svg?react";
import AcandemyIcon from "../assets/header/nav/acandemy.svg?react";
import ActivityIcon from "../assets/header/nav/activity.svg?react";
import AffiliatesIcon from "../assets/header/nav/affiliates.svg?react";
import BonusIcon from "../assets/header/nav/bonus.svg?react";
import BotsIcon from "../assets/header/nav/bots.svg?react";
import FuturesIcon from "../assets/header/nav/futures.svg?react";
import ConvertIcon from "../assets/header/nav/convert.svg?react";
import CoinMIcon from "../assets/header/nav/coin-m.svg?react";
import NewsIcon from "../assets/header/nav/news.svg?react";
import EducationIcon from "../assets/header/nav/education.svg?react";
import GuidesIcon from "../assets/header/nav/guides.svg?react";
import ReferralIcon from "../assets/header/nav/referral.svg?react";
import DemoIcon from "../assets/header/nav/demo.svg?react";
import FuturesCopyTradingIcon from "../assets/header/nav/futures-copy-trading.svg?react";
import SpotCopyTradingIcon from "../assets/header/nav/spot-copy-trading.svg?react";
import ApplicationIcon from "../assets/header/nav/application.svg?react";
import VipIcon from "../assets/header/nav/vip.svg?react";
import TokenSplashIcon from "../assets/header/nav/token-splash.svg?react";
import useDemoTradingListen from "../../../Header/hooks/useDemoTradingListen";
import Cookies from "js-cookie";
import USDCIcon from "../assets/header/nav/usdc.svg?react";
import QuickBuyIcon from "../assets/header/nav/quick-buy.svg?react";
import BankTransferIcon from "../assets/header/nav/bank-transfer.svg?react";
import CryptoDptIcon from "../assets/header/nav/crypto-dpt.svg?react";
import WalletIcon from "../assets/header/nav/wallet-fill.svg?react";
import RecurringIcon from "../assets/header/nav/recurring-buy.svg?react";
import SimpleEarnIcon from "../assets/header/nav/simple-earn.svg?react";
import RWAEarnIcon from "../assets/header/nav/rwa-earn.svg?react";
import DualInvestmentIcon from "../assets/header/nav/dual-invest.svg?react";
import { buildPinnedTail } from "../config/pinnedActivities";
import LaunchpadIcon from "../assets/header/nav/launchpad.svg?react";
import PreIpoIcon from "../assets/header/nav/pre-ipo.svg?react";
import OTCIcon from "../assets/header/nav/otc.svg?react";
import { BUITheme } from "@blofin/blofin-ui";

const filterMoreColumn = (
  column: { [key: string]: MenuProps[] },
  key: keyof MenuProps,
  isExclude: boolean
): { [key: string]: MenuProps[] } => {
  const filtered: Record<string, MenuProps[]> = {};
  for (const [category, items] of Object.entries(column)) {
    // __widget__ 等内部约定 key 不做过滤
    if (category.startsWith("__")) {
      filtered[category] = items;
      continue;
    }
    if (Array.isArray(items)) {
      const filteredItems = items.filter((subItem) => (isExclude ? !subItem[key] : subItem[key]));
      if (filteredItems.length > 0) {
        filtered[category] = filteredItems;
      }
    } else {
      filtered[category] = items;
    }
  }
  return filtered;
};

const filterNavByKey = (nav: MenuProps[], key: keyof MenuProps, isExclude = false) => {
  return nav
    .map((item) => {
      if (item.items) {
        return {
          ...item,
          items: item.items.filter((subItem) => (isExclude ? !subItem[key] : subItem[key]))
        };
      }

      if (item.type === "menu" && item.more) {
        // 数组格式：逐列过滤
        if (Array.isArray(item.more)) {
          const filteredMore = item.more
            .map((column) => filterMoreColumn(column, key, isExclude))
            .filter((col) => Object.keys(col).length > 0);
          return { ...item, more: filteredMore };
        }

        // 对象格式：向后兼容
        return {
          ...item,
          more: filterMoreColumn(item.more as { [key: string]: MenuProps[] }, key, isExclude)
        };
      }

      return item;
    })
    .filter((item) => (isExclude ? !item[key] : item[key]));
};

const useNav: (theme?: BUITheme) => { navs: MenuProps[]; pinnedTail: MenuProps[] } = (theme) => {
  const {
    localeLink,
    isLogin,
    userInfo,
    baseUrl,
    env,
    userAccountType,
    userViewType,
    copyTradeUserInfo
  } = useStoreState();

  const { isUk } = useColor(baseUrl, env);

  const { isChile } = useChileCheck(baseUrl);

  const { triggerDemoTradingChange } = useDemoTradingListen();

  const copyTradeRole = Cookies.get("role") || copyTradeUserInfo?.role;
  const isTrader = `${copyTradeRole}` === "1";

  const navs = useMemo(() => {
    let navs: MenuProps[] = [
      {
        type: "menu",
        label: "crypto",
        drawer: false,
        icon: CryptoIcon,
        isSupportDemoTrading: false,
        isSupportSubAccount: false,
        activeRouts: [
          "/crypto/[[...currency]]",
          "/crypto/app-confirm",
          "/crypto/confirm",
          "/crypto/history",
          "/crypto/otc"
        ],
        items: [
          {
            label: "quickBuy",
            url: `crypto`,
            des: "quickBuyDes",
            icon: QuickBuyIcon,
            extraBadges: ["Promo"]
          },
          {
            label: "bankTransfer",
            url: `crypto/buy-USDT/EUR`,
            des: "bankTransferDes",
            icon: BankTransferIcon
          },
          {
            label: "recurring",
            url: `crypto/recurring`,
            des: "recurringDes",
            icon: RecurringIcon,
            extraBadges: ["Promo"]
          },
          {
            label: "cryptoDpt",
            url: `assets/deposit`,
            des: "cryptoDptDes",
            icon: CryptoDptIcon,
            clickFunc: (e) => {
              e.stopPropagation();
              e.preventDefault();
              if (!isLogin) {
                window.location.href = `${localeLink}/login?redirect=${encodeURIComponent(
                  `${localeLink}/assets/deposit`
                )}`;
              } else {
                window.location.href = `${localeLink}/assets/deposit`;
              }
            }
          },
          {
            label: "otc",
            url: `crypto/otc`,
            des: "otcDes",
            icon: OTCIcon,
            basePath: ""
          }
        ]
      },
      {
        type: "link",
        label: "market",
        url: `${localeLink}/market/derivatives`,
        activeRouts: ["/market/[tab]"],
        icon: MarketIcon,
        isSupportSubAccount: true
      },
      {
        type: "menu",
        label: "spot",
        drawer: false,
        icon: SpotMainIcon,
        isSupportDemoTrading: true,
        isSupportSubAccount: true,
        activeRouts: [
          "/spot/[marketID]",
          "/spot/[marketID]/copy",
          "/token-splash",
          "/token-splash/details",
          "/launchpad",
          "/launchpad/[project_id]",
          "/launchpad/records",
          "/pre-ipo",
          "/pre-ipo/[project_id]",
          "/pre-ipo/records"
        ],
        more: [
          // 左列：Trading + Explore 分组
          {
            trading: [
              {
                label: "spot",
                url: "BTC-USDT",
                des: "spotDes",
                icon: SpotIcon,
                basePath: "spot",
                isSupportDemoTrading: true,
                isSupportSubAccount: true,
                isPrimary: true,
                clickFunc: (e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  sessionStorage.removeItem("global-role");
                  setTimeout(() => {
                    window.location.href = `/spot${localeLink}/BTC-USDT`;
                  }, 100);
                }
              },
              {
                label: "convert",
                url: "convert",
                des: "convertDes",
                icon: ConvertIcon,
                isSupportDemoTrading: false,
                isSupportSubAccount: false
              }
            ],
            explore: [
              {
                label: "bots",
                url: `bots-aggregation`,
                des: "botsDes",
                icon: BotsIcon,
                basePath: "futures",
                isPrimary: true,
                widgetType: "botProducts" as const,
                isSupportDemoTrading: true,
                clickFunc: (e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  sessionStorage.removeItem("global-role");
                  setTimeout(() => {
                    window.location.href = `/futures${localeLink}/bots-aggregation`;
                  }, 100);
                }
              },
              {
                label: "tokenSplash",
                url: "token-splash",
                des: "tokenSplashDes",
                icon: TokenSplashIcon,
                isSupportDemoTrading: false,
                isSupportSubAccount: false
              },
              {
                label: "launchpad",
                url: "launchpad",
                des: "launchpadDes",
                icon: LaunchpadIcon,
                isSupportDemoTrading: false,
                isSupportSubAccount: false
              },
              {
                label: "preIpo",
                url: "pre-ipo",
                des: "preIpoDes",
                icon: PreIpoIcon,
                isSupportDemoTrading: false,
                isSupportSubAccount: false,
                isExcludeChile: true,
                extraBadges: ["NEW"]
              }
            ]
          },
          // 右列：搜索行情 widget
          {
            __widget__: [{ label: "" as any, widgetType: "searchMarket" as const }]
          }
        ]
      },
      {
        type: "menu",
        label: "futures",
        activeRouts: ["/futures/[marketID]"],
        drawer: false,
        icon: FuturesMainIcon,
        recommend: true,
        isSupportDemoTrading: true,
        isSupportSubAccount: true,
        more: [
          // 左列：Trading + Explore 分组
          {
            trading: [
              {
                label: "usdtM",
                url: `BTC-USDT`,
                des: "futuresDes",
                icon: FuturesIcon,
                basePath: "futures",
                isPrimary: true,
                isSupportDemoTrading: true,
                isSupportSubAccount: true
              },
              {
                label: "usdcM",
                url: `BTC-USDC`,
                des: "usdcMDes",
                icon: USDCIcon,
                basePath: "futures",
                isPrimary: true,
                isSupportDemoTrading: true,
                isSupportSubAccount: true
              },
              {
                label: "coinM",
                url: `BTC-USD`,
                des: "coinMDes",
                icon: CoinMIcon,
                basePath: "futures",
                isPrimary: true,
                isSupportDemoTrading: true,
                isSupportSubAccount: true
              }
            ],
            explore: [
              {
                label: "bots",
                url: `bots-aggregation`,
                des: "botsDes",
                icon: BotsIcon,
                basePath: "futures",
                isPrimary: true,
                widgetType: "botProducts" as const,
                isSupportDemoTrading: true,
                clickFunc: (e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  sessionStorage.removeItem("global-role");
                  setTimeout(() => {
                    window.location.href = `/futures${localeLink}/bots-aggregation`;
                  }, 100);
                }
              },
              {
                label: "demoTrading",
                url: `BTC-USDT`,
                des: "demoTradingDesc",
                icon: DemoIcon,
                basePath: "futures",
                clickFunc: (e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  sessionStorage.removeItem("global-role");
                  triggerDemoTradingChange(true);

                  if (!isLogin) {
                    window.location.href = `${localeLink}/login?isDemoTrading=1&redirect=${encodeURIComponent(
                      `/futures${localeLink}/BTC-USDT`
                    )}`;
                    return;
                  }
                  Cookies.set("Global-UserViewType", "demo-trading", { expires: 365 });
                  setTimeout(() => {
                    if (
                      window.location.pathname.includes("futures") ||
                      (window.location.pathname.startsWith("/activity") &&
                        window.location.pathname.includes("/d-"))
                    ) {
                      window.location.reload();
                    } else {
                      window.location.href = `/futures${localeLink}/BTC-USDT`;
                    }
                  }, 100);
                }
              }
            ]
          },
          // 右列：搜索行情 widget
          {
            __widget__: [{ label: "" as any, widgetType: "searchMarket" as const }]
          }
        ]
      },
      {
        type: "link",
        label: "copyTrading",
        // url: `${localeLink}/copy-trade`,
        icon: CopyTradingIcon,
        activeRouts: [
          "/copy-trade",
          "/copy-trade/leader",
          "/copy-trade/follower",
          "/copy-trade/assets",
          "/copy-trade/application",
          "/copy-trade/details",
          "/copy-trade/details/[leaderID]"
        ],
        isSupportSubAccount: isTrader,
        more: [
          {
            __items__: [
              {
                label: "futuresCopyTrading",
                url: "copy-trade/futures?tab=allTraders",
                des: "futuresCopyTradingDes",
                icon: FuturesCopyTradingIcon,
                isSupportSubAccount: isTrader,
                isPrimary: true,
                widgetType: "leaderboard" as const
              },
              {
                label: "spotCopyTrading",
                url: "copy-trade/spot",
                des: "spotCopyTradingDes",
                icon: SpotCopyTradingIcon,
                isSupportSubAccount: isTrader,
                isPrimary: true,
                widgetType: "leaderboard" as const,
                extraBadges: ["NEW"]
              },
              {
                label: "eliteTradersRecruitment",
                url: "copy-trade/application?from=heading",
                des: "eliteTradersRecruitmentDes",
                icon: ApplicationIcon
              }
            ]
          },
          {
            __widget__: [{ label: "" as const, widgetType: "leaderboard" as const }]
          }
        ]
      },
      {
        type: "menu",
        label: "earn",
        drawer: false,
        icon: EarnIcon,
        activeRouts: ["/earn", "/earn/my", "/earn/rwa-rwusd", "/earn/dual-investment"],
        items: [
          {
            label: "simpleEarn",
            url: "earn",
            des: "simpleEarnDes",
            icon: SimpleEarnIcon,
            basePath: "",
            isSupportDemoTrading: false,
            isSupportSubAccount: false
          },
          {
            label: "rwaEarn",
            url: "earn/rwa-rwusd",
            des: "rwaEarnDes",
            icon: RWAEarnIcon,
            isSupportDemoTrading: false,
            isSupportSubAccount: false
          },
          {
            label: "dualInvestment",
            url: "earn/dual-investment",
            des: "dualInvestmentDes",
            icon: DualInvestmentIcon,
            isSupportDemoTrading: false,
            isSupportSubAccount: false
          }
        ]
      },
      {
        type: "link",
        label: "welcomeBonus",
        url: `${localeLink}/task-center`,
        activeRouts: ["/task-center"],
        icon: BonusIcon
      },
      {
        type: "menu",
        label: "more",
        drawer: false,
        icon: MoreIcon,
        isSupportSubAccount: true,
        isSupportDemoTrading: false,
        activeRouts: [
          "/activity",
          "/activity/competition/[activityID]",
          "/activity/details/[50]",
          "/activity/details/[51]",
          "/activity/regular/[activityID]",
          "/affiliates",
          "/referral",
          "/ambassador",
          "/news",
          "/news/[title]",
          "/academy",
          "/academy/[[...slug]]",
          "/academy/[type]/[title]",
          "/vip"
        ],
        more: [
          // 第一列：Promo
          {
            promo: [
              {
                label: "activity",
                url: "",
                des: "activityDes",
                basePath: "activity",
                icon: ActivityIcon,
                isSupportDemoTrading: false
              },
              {
                label: "affiliates",
                url: "affiliates",
                des: "affiliatesDes",
                icon: AffiliatesIcon
              },
              {
                label: "referral",
                url: "referral?source=more",
                des: "referralDes",
                icon: ReferralIcon,
                isExcludeUk: true
              },
              {
                label: "vip",
                url: "vip",
                des: "vipDes",
                icon: VipIcon
              }
            ]
          },
          // 第二列：Insights
          {
            insights: [
              {
                label: "academy",
                url: "academy",
                des: "academyNavDes",
                icon: AcandemyIcon,
                isSupportSubAccount: true
              },
              {
                label: "news",
                url: "news",
                des: "newsNavDes",
                icon: NewsIcon,
                isSupportSubAccount: true
              },
              {
                label: "education",
                url: "academy/education",
                des: "educationNavDes",
                icon: EducationIcon,
                isSupportSubAccount: true
              },
              {
                label: "blofinGuides",
                url: "academy/guides",
                des: "blofinGuidesNavDes",
                icon: GuidesIcon,
                isSupportSubAccount: true
              }
            ]
          },
          // 第三列：Product
          {
            product: [
              {
                label: "wallet",
                url: "https://wallet.blofin.com/en",
                des: "walletDes",
                icon: WalletIcon,
                extraBadges: ["NEW"],
                outbound: true,
                clickFunc: (e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  window.location.href = "https://wallet.blofin.com/en";
                }
              }
            ]
          }
        ]
      } as MenuProps
    ];

    if (isUk) {
      navs = filterNavByKey(navs, "isExcludeUk", true);
    }

    if (isChile) {
      navs = filterNavByKey(navs, "isExcludeChile", true);
    }

    const isDemoTrading = userViewType === "demo-trading";

    if (isDemoTrading) {
      navs = filterNavByKey(navs, "isSupportDemoTrading").concat([
        {
          label: "activity",
          url: "/activity",
          activeRouts: ["/activity", "/activity/", "/activity/competition/specific/[activityID]"],
          type: "link",
          isSupportDemoTrading: true
        }
      ]);
    }

    if (
      userInfo?.account_type === "NORMAL_SUB_ACCOUNT" ||
      userAccountType === "NORMAL_SUB_ACCOUNT"
    ) {
      navs = filterNavByKey(navs, "isSupportSubAccount");
    }

    return navs;
  }, [
    localeLink,
    isUk,
    isChile,
    userInfo?.account_type,
    triggerDemoTradingChange,
    isLogin,
    isTrader,
    userAccountType,
    userViewType
  ]);

  // 钉在 nav 列表尾部、不参与 overflow 收起的活动入口，集中由 config/pinnedActivities 管理。
  // 替换或下线活动只改配置文件即可。
  const pinnedTail = useMemo<MenuProps[]>(
    () =>
      buildPinnedTail({
        theme,
        localeLink,
        isUk,
        isDemoTrading: userViewType === "demo-trading"
      }),
    [localeLink, isUk, userViewType, theme]
  );

  return { navs, pinnedTail };
};

export default useNav;
